/*
* Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
*/
import React, {
  memo,
  MouseEvent,
  MouseEventHandler,
  useCallback,
  useId,
  useMemo
} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import KeyboardArrowDown from '@material-ui/icons/KeyboardArrowDown';
import classnames from 'classnames';
import useElementResize from '@ion/core/src/hooks/useElementResize';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import {ColumnChooserButton} from '../../components/buttons/ColumnChooserButton';
import {CSVExportButton} from '../../components/buttons/CSVExportButton';
import {GridClearFiltersButton} from '../../components/buttons/GridClearFiltersButton';
import {
  IconButton,
  IconButtonLabelPlacement
} from '../../components/buttons/IconButton';
import {PlayPauseButton} from '../../components/buttons/PlayPauseButton';
import {SaveIconButton} from '../../components/buttons/SaveIconButton';
import useMedia from '../../hooks/useMedia';
import {BottomGridViewIcon} from '../../icons/BottomGridViewIcon';
import {ColumnChooserIcon} from '../../icons/ColumnChooserIcon';
import {SideGridViewIcon} from '../../icons/SideGridViewIcon';
import {SystemSortIcon} from '../../icons/SystemSortIcon';
import {SystemViewCardsIcon} from '../../icons/SystemViewCardsIcon';
import {SystemViewListIcon} from '../../icons/SystemViewListIcon';
import Hidden from '../../layout/Hidden';
import Render from '../../render/Render';
import {toggleThemeStyle} from '../../theme/helpers';
import {dlsColor} from '../../theme/theme';
import useThemeContext from '../../theme/useThemeContext';
import {DLSTheme, IconSize, SideSheetView} from '../../types/types';
import {LabelTypography} from '../../typography/LabelTypography';
import LabelIconButton from '../buttons/LabelIconButton';
import RefreshGridButton from '../buttons/RefreshGridButton';
import GridControlsBank from '../grid/GridControlsBank';
import {PulseLoader} from '../loading/PulseLoader';
import {SideSheetArrow} from '../sidesheet/SideSheetArrow';
import {
  AssetUpdatesControl,
  AssetUpdatesControlProps
} from './current/AssetUpdatesControl';
import SidesheetModeMenu from './SidesheetModeMenu';

interface StyleProps {
  backgroundColor?: string;
};

const useStyles = makeStyles((theme: DLSTheme) => ({
  root : {
    width                          : '100%',
    minWidth                       : '320px',
    height                         : toggleThemeStyle('48px', '60px'),
    display                        : 'flex',
    alignItems                     : 'center',
    flexDirection                  : 'row',
    backgroundColor                : ({backgroundColor}: StyleProps) => backgroundColor || dlsColor('white'),
    alignContent                   : 'space-between',
    [theme.breakpoints.down('sm')] : {
      minWidth : 0
    }
  },
  leftIcons : {
    display    : 'flex',
    alignItems : 'center',
    flexWrap   : 'nowrap'
  },
  gridOptions : {
    display    : 'flex',
    alignItems : 'center'
  },
  resultsWrapper : {
    borderTop : `1px solid ${dlsColor('grey5')}`
  },
  margin : {
    marginRight : '50px'
  },
  collapseIconWrapper : {
    display        : 'flex',
    width          : '30%',
    justifyContent : 'flex-start',
    alignItems     : 'center'
  },
  actionsIcon : {
    width : '70%'
  },
  labelStyle : {
    marginRight : '8px',
    minWidth    : '104px'
  },
  listActive : {
    background : theme.dls.alt.components.btn.iconBackground
  },
  sortIcon : {
    display        : 'inline-flex',
    justifyContent : 'left',
    margin         : '12px'
  },
  divStyle : {
    display        : toggleThemeStyle('inline-flex', 'block'),
    alignItems     : 'center',
    justifyContent : 'space-between',
    flexGrow       : 1,
    height         : toggleThemeStyle('48px', '44px'),
    marginLeft     : toggleThemeStyle('12px', '0')
  },
  sideSheetOpenStyle : {
    justifyContent : 'space-around'
  },
  flexContainer : {
    display : 'flex'
  }
}));

export const THRESHOLD_WIDTH_WITHOUT_FILTER = 728;
export const THRESHOLD_WITDH                = 648;

export interface ResultsModuleProps extends StyleProps {
  onSort?: (event: MouseEvent) => void;
  onView?: (event: MouseEvent) => void;
  onFilter?: (event: MouseEvent) => void;
  onRefresh?: (event: MouseEvent) => void;
  label?: string;
  customLabel?: string | React.JSX.Element;
  sideSheetView?: SideSheetView;
  isRefreshEnabled?: boolean;
  mobileSideSheetClose?: MouseEventHandler<HTMLButtonElement>;
  mobileSideSheetOpen?: boolean;
  showModeSelection?: boolean;
  fullHeight?: boolean;
  isTableViewEnabled?: boolean;
  gridOptions?: GenericObject;
  onToggleClick?: Function;
  onAssetUpdatesPlayback?: Function;
  isDesktopSideSheetOpen?: boolean;
  isAssetUpdatesEnabled?: boolean;
  isTracking2?: boolean;
  updateProgress?: AssetUpdatesControlProps;
}

/**
 * @ResultsModule
 *
 * Defines Result Module component. This component is reusable and can be custom
 * rendered with props.In Desktop view sort icon and label will be visible. Where as in
 * Mobile view Sort icon, filter icon and Label will be displayed.
 *
 * Figma link:
 * https://www.figma.com/file/srjlHrDuM90sCoaOwpjaDU/02_DLS_Components?node-id=1588%3A1717
 *
 * ```typescript
 * <ResultsModule label={labels} onFilter={onFilterView} onSort={onSort} />
 *
 * ```
 */
export const ResultsModule = ({
  label,
  customLabel,
  onView,
  onSort,
  onRefresh,
  sideSheetView,
  isRefreshEnabled,
  isTableViewEnabled,
  mobileSideSheetClose,
  backgroundColor,
  gridOptions,
  onToggleClick,
  updateProgress,
  onAssetUpdatesPlayback,
  isDesktopSideSheetOpen,
  showModeSelection = true,
  mobileSideSheetOpen = false,
  isAssetUpdatesEnabled = true,
  isTracking2 = false,
  fullHeight = false
}: ResultsModuleProps) => {
  const classes              = useStyles({backgroundColor});
  const {isPhone}            = useMedia();
  const isBottomTable        = sideSheetView === SideSheetView.bottomTable;
  const iconSize             = isBottomTable ? IconSize.medium : IconSize.small;
  const playPauseButtonLabel = translate('common:liveUpdates.label');
  const playTooltipText      = translate('common:liveUpdates.play.tooltipText');
  const pauseTooltipText     = translate('common:liveUpdates.pause.tooltipText');

  const {
    isLegacy : isLegacyTheme,
    isCurrent : isCurrentTheme
  } = useThemeContext();

  const {
    onExport,
    gridState,
    isExporting,
    onClearFilters,
    isFilterApplied,
    autoSaveGridOption,
    updateGridSettings,
    onShowColumnChooser
  } = gridOptions || {};

  const rootId    = useId();
  const {width}   = useElementResize(rootId);
  const showLabel = isTableViewEnabled && (
    !!width && width > (isFilterApplied ? THRESHOLD_WITDH : THRESHOLD_WIDTH_WITHOUT_FILTER)
  );

  const isActiveColor = useCallback((id) => (
    id === sideSheetView ? dlsColor('caBlue') : dlsColor('grey3')
  ), [sideSheetView]);

  const isSelected = useCallback((...ids) => (
    ids.some(id => id === sideSheetView)
  ), [sideSheetView]);

  const isTableView = useMemo(() => isSelected(SideSheetView.table, SideSheetView.bottomTable), [isSelected]);

  const onOpenColumnChooser = useCallback(function() {
    onShowColumnChooser?.(true);
  }, [onShowColumnChooser]);

  const assetUpdatesProgress = useMemo(() => updateProgress || {
    isAssetUpdatesEnabled,
    onAssetUpdatesPlayback : onAssetUpdatesPlayback as any
  }, [
    updateProgress,
    isAssetUpdatesEnabled,
    onAssetUpdatesPlayback
  ]);

  return (
    <>
      {(fullHeight && isPhone) ? (
        <div className={classnames(classes.root, isPhone ? classes.resultsWrapper : null)}>
          <div className={classnames(classes.divStyle, mobileSideSheetOpen ? classes.sideSheetOpenStyle : null)}>
            <Hidden show='mobile'>
              {mobileSideSheetOpen && (
                <IconButton className={isRefreshEnabled ? null : classes.margin}
                  onClick={mobileSideSheetClose}
                  icon={<KeyboardArrowDown />} />)}
              {isRefreshEnabled && (<RefreshGridButton onRefresh={onRefresh} />)}
            </Hidden>
            <Render.If condition={isLegacyTheme}>
              <div className={classes.labelStyle}>
                {label !== 'loading' ? (
                  <LabelTypography>{label || null}</LabelTypography>
                ): (
                  <PulseLoader />
                )}
              </div>
            </Render.If>
            <div className={classes.flexContainer}>
              <Hidden show='mobile'>
                <IconButton id={SideSheetView.detailed}
                  onClick={onView}
                  color={isActiveColor(SideSheetView.detailed)}
                  icon={<SystemViewListIcon size={IconSize.small}/>} />
                <IconButton id={SideSheetView.collapsed}
                  onClick={onView}
                  color={isActiveColor(SideSheetView.collapsed)}
                  icon={<SystemViewCardsIcon size={IconSize.small} />} />
                {isTableViewEnabled && (
                  <IconButton id={SideSheetView.table}
                    onClick={onView}
                    color={isActiveColor(SideSheetView.table)}
                    icon={<SideGridViewIcon size={IconSize.small} /> } />)}
                <IconButton onClick={onSort} icon={<SystemSortIcon />} />
              </Hidden>
              <Hidden show='desktop'>
                {isRefreshEnabled && (<RefreshGridButton onRefresh={onRefresh} />)}
                <IconButton id={SideSheetView.detailed}
                  onClick={onView}
                  color={isActiveColor(SideSheetView.detailed)}
                  icon={<SystemViewListIcon size={IconSize.small}/>} />
                <IconButton id={SideSheetView.collapsed}
                  onClick={onView}
                  color={isActiveColor(SideSheetView.collapsed)}
                  icon={<SystemViewCardsIcon size={IconSize.small} />} />
                {isTableViewEnabled && (
                  <>
                    <IconButton id={SideSheetView.table}
                      onClick={onView}
                      color={isActiveColor(SideSheetView.table)}
                      icon={<SideGridViewIcon size={IconSize.small} /> } />
                    <IconButton id={SideSheetView.bottomTable}
                      onClick={onView}
                      color={isActiveColor(SideSheetView.bottomTable)}
                      icon={<BottomGridViewIcon size={IconSize.small} /> } />
                  </>
                )}
                <IconButton onClick={onSort}
                  icon={<SystemSortIcon size={IconSize.small} />} />
              </Hidden>
            </div>
          </div>
        </div>
      ) : (
        <div className={classes.root} id={rootId}>
          <Hidden show='mobile'>
            <div className={classes.sortIcon}>
              {isRefreshEnabled && (<RefreshGridButton onRefresh={onRefresh} />)}
              <IconButton onClick={onSort} icon={<SystemSortIcon />} />
            </div>
          </Hidden>
          <div className={classes.divStyle}>
            <Render.If condition={isLegacyTheme}>
              {customLabel ||
              <div className={classes.labelStyle}>
                {label !== 'loading' ? (
                  <LabelTypography>{label || null}</LabelTypography>
                ): (
                  <PulseLoader />
                )}
              </div>
              }
            </Render.If>
            <div className={classes.leftIcons}>
              <Hidden show='mobile'>
                <IconButton id={SideSheetView.detailed}
                  onClick={onView}
                  color={isActiveColor(SideSheetView.detailed)}
                  icon={<SystemViewListIcon size={IconSize.small}/>} />
                <IconButton id={SideSheetView.collapsed}
                  onClick={onView}
                  color={isActiveColor(SideSheetView.collapsed)}
                  icon={<SystemViewCardsIcon size={IconSize.small} />} />
                {isTableViewEnabled && (
                  <IconButton id={SideSheetView.table}
                    onClick={onView}
                    color={isActiveColor(SideSheetView.table)}
                    icon={<SideGridViewIcon size={IconSize.small} /> } />)}
              </Hidden>
              <Hidden show='desktop'>
                <Render.If condition={isTracking2}>
                  <Render.If condition={isCurrentTheme}>
                    <AssetUpdatesControl height='44px'
                      updateProgress={assetUpdatesProgress} />
                  </Render.If>
                  <Render.If condition={isLegacyTheme}>
                    <PlayPauseButton onTogglePlayState={onAssetUpdatesPlayback as any}
                      isPlaying={isAssetUpdatesEnabled}
                      showLabel={showLabel as any}
                      iconSize={iconSize}
                      label={playPauseButtonLabel}
                      playTooltipText={playTooltipText}
                      pauseTooltipText={pauseTooltipText} />
                  </Render.If>
                </Render.If>
                <Render.If condition={isRefreshEnabled}>
                  <LabelIconButton label={translate('common:refresh')} displayLabel={showLabel}>
                    <RefreshGridButton onRefresh={onRefresh as any}
                      showCurrentLabel={false}
                      disableTooltip={showLabel as boolean} />
                  </LabelIconButton>
                </Render.If>
                <Render.If condition={showModeSelection}>
                  <LabelIconButton label={translate('common:layout')} displayLabel={showLabel}>
                    <SidesheetModeMenu onView={onView} size={iconSize} sideSheetView={sideSheetView} />
                  </LabelIconButton>
                </Render.If>
                {!isTableView && (
                  <IconButton onClick={onSort} icon={<SystemSortIcon size={iconSize} />} />
                )}
              </Hidden>
            </div>
          </div>
          <Render.If condition={isTableView}>
            <GridControlsBank controlsClassName={classes.gridOptions}>
              <Render>
                <Render.If condition={isCurrentTheme}>
                  <GridClearFiltersButton onClearFilters={onClearFilters}
                    areFiltersApplied={isFilterApplied}
                    currentLabelPlacement={IconButtonLabelPlacement.HORIZONTAL} />
                  <CSVExportButton isExporting={isExporting}
                    onExport={onExport}
                    exportLabel={translate('common:export.label')}
                    currentLabelPlacement={IconButtonLabelPlacement.HORIZONTAL} />
                  <ColumnChooserButton onShowColumnChooser={onOpenColumnChooser}
                    currentLabelPlacement={IconButtonLabelPlacement.HORIZONTAL} />
                  <Render.If condition={!autoSaveGridOption}>
                    <SaveIconButton type={gridState as any}
                      onClick={updateGridSettings}
                      currentLabelPlacement={IconButtonLabelPlacement.HORIZONTAL} />
                  </Render.If>
                </Render.If>
                <Render.Else>
                  <LabelIconButton label={translate('common:filters.clearFilters')} displayLabel={showLabel && isFilterApplied}>
                    <GridClearFiltersButton size={iconSize}
                      onClearFilters={onClearFilters}
                      areFiltersApplied={isFilterApplied}
                      showCurrentLabel={false}
                      disableTooltip={showLabel as boolean} />
                  </LabelIconButton>
                  <LabelIconButton label={translate('common:export.label')} displayLabel={showLabel}>
                    <CSVExportButton isExporting={isExporting}
                      onExport={onExport}
                      size={iconSize}
                      showCurrentLabel={false}
                      disableTooltip={showLabel as boolean}
                      currentLabelPlacement={IconButtonLabelPlacement.HORIZONTAL} />
                  </LabelIconButton>
                  <LabelIconButton label={translate('common:columnChooser.title')} displayLabel={showLabel}>
                    <IconButton onClick={function() {
                      onShowColumnChooser?.(true);
                    }}
                    label={translate('common:columnChooser.title')}
                    hideLabel
                    icon={<ColumnChooserIcon size={iconSize} /> }
                    labelPlacement={IconButtonLabelPlacement.HORIZONTAL} />
                  </LabelIconButton>
                  {!autoSaveGridOption && (
                    <LabelIconButton label={translate('common:editors.preferences.saveGrid')} displayLabel={showLabel}>
                      <SaveIconButton type={gridState as any}
                        onClick={updateGridSettings}
                        iconProps={{size: iconSize}}
                        showCurrentLabel={false}
                        disableTooltip={showLabel as boolean}
                        currentLabelPlacement={IconButtonLabelPlacement.HORIZONTAL} />
                    </LabelIconButton>
                  )}
                </Render.Else>
              </Render>
            </GridControlsBank>
          </Render.If>
          {isDesktopSideSheetOpen && <SideSheetArrow onClick={onToggleClick} isSideSheetExpanded />}
        </div>
      )}
    </>
  );
};

export default memo(ResultsModule);
